import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Shield, Award, Users, Clock } from 'lucide-react';

export const BenefitsSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const benefits = [
    {
      icon: <Shield className="h-6 w-6" />,
      title: 'Legal Ownership',
      description: 'Exclusive rights to use your trademark throughout the United States',
    },
    {
      icon: <Award className="h-6 w-6" />,
      title: 'Brand Trust',
      description: 'Build stronger credibility and confidence with customers and business partners',
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Competitive Edge',
      description: 'Stop competitors from using similar names or confusing branding',
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Quick Turnaround',
      description: 'Fast evaluation and submission within 24–48 hours',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % benefits.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [benefits.length]);

  return (
    <div className="relative" data-testid="benefits-slider">
      {/* Desktop View - All Benefits in Grid */}
      <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
        {benefits.map((benefit, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
          >
            <div className="bg-[#E1A334]/10 rounded-xl w-14 h-14 flex items-center justify-center text-[#E1A334] mb-4">
              {benefit.icon}
            </div>
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
              {benefit.title}
            </h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              {benefit.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Mobile View - Slider */}
      <div className="md:hidden">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -100 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-lg transition-all duration-300"
        >
          <div className="bg-[#E1A334]/10 rounded-xl w-14 h-14 flex items-center justify-center text-[#E1A334] mb-4">
            {benefits[currentIndex].icon}
          </div>
          <h3 className="font-heading font-bold text-lg text-slate-900 mb-2">
            {benefits[currentIndex].title}
          </h3>
          <p className="text-slate-600 text-sm leading-relaxed">
            {benefits[currentIndex].description}
          </p>
        </motion.div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6">
          {benefits.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex ? 'w-8 bg-[#E1A334]' : 'w-2 bg-slate-300'
              }`}
              data-testid={`benefits-dot-${index}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
